import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    FormControlLabel,
    Checkbox,
    withStyles,
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Grid,
    RadioGroup,
    Radio,
    CircularProgress
} from '@material-ui/core';
import { unmountComponentAtNode } from 'react-dom';
import { settingsStyles } from '../styles';
import { TC_MERGE_TAGS, TC_MODAL_MERGE_TAGS } from '../../../collums-constants/index';

import Editor from './components/Editor';
import { Controller } from 'react-hook-form';
import InfoTooltip from '../../common/InfoTooltip';
import { late_cancellation_template } from '../../../collums-constants/utils';

export const DEPOSIT_TYPES = {
    NO_DEPOSIT: 'No deposit',
    DEFAULT: 'Default deposit',
    PERCENTAGE: 'Percentage',
    FIXED_AMOUNT: 'Fixed amount'
};

export const MINIMISE_GAPS_STRATEGY = {
    ALWAYS_MINIMISE: 'Always minimise gaps',
    SHOW_ALL_SLOTS: 'Minimise after first booking'
};

export const BOUNDARY_SLOT_TYPES = {
    FIRST_OF_THE_DAY: 'First of the day',
    LAST_OF_THE_DAY: 'Last of the day'
};

function CustomerAppTab({
    classes,
    register,
    organisation,
    setOrganisation,
    homeBannerRef,
    termsAndConditionsRef,
    lateCancellationRef,
    thankYouRef,
    termsAndConditionsCourseRef,
    thankYouCourseRef,
    termsAndConditionsCourseServiceRef,
    thankYouCourseServiceRef,
    termsAndConditionsGiftVoucherRef,
    thankYouGiftVoucherRef,
    control,
    setBookIntervals,
    bookIntervals,
    setCancellationRules,
    cancellationRules,
    setReschedulingRule,
    reschedulingRule,
    setBookingDepositMultipleServicesRule,
    bookingDepositMultipleServicesRule,
    setBookingDepositRule,
    bookingDepositRule
}) {
    const [isFirstLoading, setIsFirstLoading] = useState(true);
    const [selectedBookingType, setSelectedBookingType] = useState('service');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [containerHeight, setContainerHeight] = useState('auto');
    const contentRef = useRef(null);

    const BOOKING_TYPES = {
        service: {
            label: 'Service Booking',
            termsField: 'termsAndConditionsText',
            thankYouField: 'thankYouText',
            termsRef: termsAndConditionsRef,
            thankYouRef: thankYouRef,
            termsTitle: 'T&C text before completing online booking',
            thankYouTitle: 'Thank you text after completing online booking'
        },
        course: {
            label: 'Course Purchase',
            termsField: 'termsAndConditionsTextCourse',
            thankYouField: 'thankYouTextCourse',
            termsRef: termsAndConditionsCourseRef,
            thankYouRef: thankYouCourseRef,
            termsTitle: 'T&C text before purchasing course online',
            thankYouTitle: 'Thank you text after purchasing course online'
        },
        courseService: {
            label: 'Course Service Booking',
            termsField: 'termsAndConditionsTextCourseService',
            thankYouField: 'thankYouTextCourseService',
            termsRef: termsAndConditionsCourseServiceRef,
            thankYouRef: thankYouCourseServiceRef,
            termsTitle: 'T&C text before completing online course service booking',
            thankYouTitle: 'Thank you text after completing online course service booking'
        },
        giftVoucher: {
            label: 'Gift Voucher Purchase',
            termsField: 'termsAndConditionsTextGiftVoucher',
            thankYouField: 'thankYouTextGiftVoucher',
            termsRef: termsAndConditionsGiftVoucherRef,
            thankYouRef: thankYouGiftVoucherRef,
            termsTitle: 'T&C Gift VOUCHER',
            thankYouTitle: 'Thank you text after completing online gift voucher purchasing'
        }
    };

    const hiddenStyle = { visibility: 'hidden', position: 'absolute', top: 0, left: 0, width: '100%' };

    const handleBookingTypeChange = async event => {
        const newBookingType = event.target.value;

        setIsTransitioning(true);
        setSelectedBookingType(newBookingType);

        await new Promise(resolve => setTimeout(resolve, 50));

        if (contentRef.current) {
            const newHeight = contentRef.current.scrollHeight;
            setContainerHeight(`${newHeight}px`);
        }

        await new Promise(resolve => setTimeout(resolve, 250));
        setIsTransitioning(false);
    };
    useEffect(() => {
        register('homeBannerText');
        register('termsAndConditionsText');
        register('thankYouText');
        register('termsAndConditionsTextCourse');
        register('thankYouTextCourse');
        register('termsAndConditionsTextCourseService');
        register('thankYouTextCourseService');
        register('termsAndConditionsTextGiftVoucher');
        register('thankYouTextGiftVoucher');
    }, [register]);

    const BOOKING_INTERVALS = [5, 10, 15, 20, 30, 45, 60];

    useEffect(() => {
        if (organisation && organisation.termsAndConditionsText && isFirstLoading) {
            // waits for the organisation to be loaded, to the editors not have empty refs
            setIsFirstLoading(false);
        }
    }, [isFirstLoading, organisation]);

    const changeHomeBannerCheckbox = useCallback(
        e => {
            const checked = e.target.checked;
            setOrganisation({ ...organisation, showHomeBannerText: checked });
            if (!checked) {
                unmountComponentAtNode(document.getElementById('home-banner-editor'));
            }
        },
        [organisation, setOrganisation]
    );

    const changeBookingDepositCheckbox = useCallback(
        e => {
            setOrganisation({
                ...organisation,
                bookingDeposit: e.target.checked
            });
        },
        [organisation, setOrganisation]
    );

    const changeBookingDepositType = useCallback(
        e => {
            setOrganisation({
                ...organisation,
                bookingDepositType: e.target.value
            });
        },
        [organisation, setOrganisation]
    );

    const changeMinimiseGaps = useCallback(
        e => {
            const minimiseGapsValue = e.target.value !== 'false';
            const strategyValue = [
                MINIMISE_GAPS_STRATEGY.ALWAYS_MINIMISE,
                MINIMISE_GAPS_STRATEGY.SHOW_ALL_SLOTS
            ].includes(e.target.value)
                ? e.target.value
                : undefined;

            setOrganisation({
                ...organisation,
                ...{ minimiseGaps: minimiseGapsValue, minimiseGapsStrategy: strategyValue }
            });
        },
        [organisation, setOrganisation]
    );

    const changeAlsoShowFirst = useCallback(
        e => {
            const value = organisation?.alsoShowFirst ? !organisation?.alsoShowFirst : e.target.checked;
            setOrganisation({
                ...organisation,
                alsoShowFirst: value
            });
        },
        [organisation, setOrganisation]
    );

    const changeAlsoShowLast = useCallback(
        e => {
            const value = organisation?.alsoShowLast ? !organisation?.alsoShowLast : e.target.checked;
            setOrganisation({
                ...organisation,
                alsoShowLast: value
            });
        },
        [organisation, setOrganisation]
    );

    const BreakLines = () => <div className={classes.breakLines} />;

    return (
        <div className={classes.customerAppSettingsContainer}>
            <Grid container direction="column" spacing={3}>
                <Grid item style={{ paddingBottom: '0' }}>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        Deposits
                    </Typography>
                </Grid>
                <Grid item>
                    <div className={classes.inlineContainer}>
                        <FormControlLabel
                            label="Allow online booking deposit"
                            control={
                                <Checkbox
                                    onChange={changeBookingDepositCheckbox}
                                    color="primary"
                                    checked={Boolean(organisation?.bookingDeposit)}
                                    inputRef={register}
                                    name="bookingDeposit"
                                />
                            }
                        />
                        <InfoTooltip
                            title={
                                'Setting this rule will apply to all services as default unless they have their own rules. You can change online deposit rules for individual services in Admin > Services.'
                            }
                        />
                    </div>

                    <FormControl className={classes.fieldContainer} variant="outlined">
                        <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                            <Typography className={organisation?.bookingDeposit ? '' : ' ' + classes.disabled}>
                                Rule for deposit
                            </Typography>
                            <Controller
                                name="bookingDepositRule"
                                className={classes.bookingDepositRule}
                                defaultValue={bookingDepositRule}
                                control={control}
                                disabled={!organisation?.bookingDeposit}
                                as={
                                    <Select onChange={() => setBookingDepositRule}>
                                        <MenuItem key="each time" value="each time">
                                            Every time
                                        </MenuItem>
                                        <MenuItem key="account difference" value="account difference">
                                            Account difference
                                        </MenuItem>
                                    </Select>
                                }
                            />
                            <InfoTooltip
                                title={
                                    <Typography>
                                        “Every time”: any deposits are required at every booking
                                        <br />
                                        “Account difference”: no payment will be required if the account total has
                                        enough funds to cover the deposit. If there are not enough funds, only the
                                        amount required to cover the deposit will be asked for.
                                    </Typography>
                                }
                            />
                        </div>
                    </FormControl>

                    <FormControl className={classes.fieldContainer} variant="outlined" style={{ marginBottom: 16 }}>
                        <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                            <Typography className={organisation?.bookingDeposit ? '' : ' ' + classes.disabled}>
                                Rule for deposit when multiple services booked
                            </Typography>
                            <Controller
                                name="bookingDepositMultipleServicesRule"
                                className={classes.bookingDepositMultipleServicesRule}
                                defaultValue={bookingDepositMultipleServicesRule}
                                control={control}
                                disabled={!organisation?.bookingDeposit}
                                as={
                                    <Select onChange={() => setBookingDepositMultipleServicesRule}>
                                        <MenuItem key="sum" value="sum">
                                            Sum
                                        </MenuItem>
                                        <MenuItem key="single" value="single">
                                            Single
                                        </MenuItem>
                                    </Select>
                                }
                            />
                            <InfoTooltip
                                title={
                                    <Typography>
                                        “Sum”: the deposit amount is the sum of deposits of every service booked.
                                        <br />
                                        “Single”: the deposit amount is the largest deposit of the services booked.
                                    </Typography>
                                }
                            />
                        </div>
                    </FormControl>

                    <div
                        className={
                            classes.inlineContainer + (organisation?.bookingDeposit ? '' : ' ' + classes.disabled)
                        }
                    >
                        <FormControl variant="h4">Default deposit rule</FormControl>
                    </div>

                    <div className={classes.inlineContainer}>
                        <RadioGroup
                            style={{ display: 'flex', flexDirection: 'row' }}
                            name="bookingDepositType"
                            inputRef={register}
                            onChange={changeBookingDepositType}
                        >
                            <FormControlLabel
                                key={`tod-${DEPOSIT_TYPES.PERCENTAGE}`}
                                value={DEPOSIT_TYPES.PERCENTAGE}
                                control={<Radio style={{ color: 'black' }} inputRef={register} />}
                                label={DEPOSIT_TYPES.PERCENTAGE}
                                checked={organisation?.bookingDepositType === DEPOSIT_TYPES.PERCENTAGE}
                                disabled={!organisation?.bookingDeposit}
                            />
                            <FormControlLabel
                                key={`tod-${DEPOSIT_TYPES.FIXED_AMOUNT}`}
                                value={DEPOSIT_TYPES.FIXED_AMOUNT}
                                control={<Radio style={{ color: 'black' }} inputRef={register} />}
                                label={DEPOSIT_TYPES.FIXED_AMOUNT}
                                checked={organisation?.bookingDepositType === DEPOSIT_TYPES.FIXED_AMOUNT}
                                disabled={!organisation?.bookingDeposit}
                            />
                        </RadioGroup>
                    </div>

                    <div className={classes.inlineContainer}>
                        <FormControl
                            variant="h4"
                            style={{ marginBottom: '5px', marginRight: '16px' }}
                            className={organisation?.bookingDeposit ? '' : ' ' + classes.disabled}
                        >
                            Deposit percentage/amount
                        </FormControl>
                        <InfoTooltip
                            title={'Enter 100% to ensure services are paid for in full at the time of booking online.'}
                        />
                    </div>
                    <div className={classes.inlineContainer}>
                        <FormControl className={classes.fieldContainer}>
                            <TextField
                                id="bookingDepositAmount-input"
                                name="bookingDepositAmount"
                                variant="outlined"
                                value={organisation?.bookingDepositAmount}
                                onChange={({ target }) => {
                                    setOrganisation({
                                        ...organisation,
                                        bookingDepositAmount: target.value,
                                        bookingDeposit: true
                                    });
                                }}
                                type="number"
                                InputProps={{
                                    inputProps: { min: 0 }
                                }}
                                inputRef={register}
                                style={{ width: '240px' }}
                                disabled={!organisation?.bookingDeposit}
                            />
                        </FormControl>
                    </div>
                </Grid>
            </Grid>

            <Grid item style={{ margin: '20px 0px 8px' }}>
                <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                    Online booking rules
                </Typography>
            </Grid>

            <FormControl variant="outlined" className={`${classes.intervalContainer}`}>
                <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                    <Typography>Offer online booking intervals every</Typography>
                    <Controller
                        name="bookingIntervals"
                        className={classes.intervalSelect}
                        defaultValue={bookIntervals}
                        control={control}
                        as={
                            <Select onChange={() => setBookIntervals}>
                                {BOOKING_INTERVALS.map(el => {
                                    return (
                                        <MenuItem key={el} value={el}>
                                            {`${el} mins`}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        }
                    />
                </div>
            </FormControl>

            <FormControl className={classes.fieldContainer} variant="outlined">
                <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                    <Typography>Do not allow online bookings</Typography>
                    <Controller
                        name="minTimeBefore"
                        className={classes.minTimeBefore}
                        defaultValue={organisation.minTimeBefore}
                        control={control}
                        as={
                            <TextField
                                id="minTimeBefore-number"
                                type="number"
                                variant="outlined"
                                InputProps={{
                                    inputProps: { min: 0 }
                                }}
                                onKeyDown={e => {
                                    if (e.keyCode === 109 || e.keyCode === 107) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        }
                    />
                    <Typography style={{ marginRight: '16px' }}>min before appointment.</Typography>
                    <InfoTooltip
                        title={
                            'Use this feature to prevent last minute online bookings that you may not be prepared for. Enter the minimum number of minutes that a client can book before their appointment time.'
                        }
                    />
                </div>
            </FormControl>

            <FormControl className={classes.fieldContainer} variant="outlined">
                <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                    <Controller
                        name="cancellationRules"
                        className={classes.cancellationSelect}
                        defaultValue={cancellationRules}
                        control={control}
                        as={
                            <Select onChange={() => setCancellationRules}>
                                <MenuItem key="do-not-allow" value="do-not-allow">
                                    Do not allow
                                </MenuItem>
                                <MenuItem key="allow" value="allow">
                                    Allow
                                </MenuItem>
                                <MenuItem key="late" value="late">
                                    Allow late
                                </MenuItem>
                            </Select>
                        }
                    />
                    <Typography style={{ marginRight: '16px' }}> cancellation online.</Typography>
                    <InfoTooltip
                        title={
                            'If you allow late cancellation online, when a client attempts to do this, they will see the Late cancellation warning text that you set below. The client must acknowledge this message by clicking a button before being able to complete the process. You will receive an automated email in the case of late cancellation (if you have the notification switched on).\n' +
                            'Deposits / late cancellation fees are not taken automatically - they must always be done manually.'
                        }
                    />
                </div>
            </FormControl>

            <FormControl className={classes.fieldContainer} variant="outlined">
                <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                    <Controller
                        name="reschedulingRule"
                        className={classes.cancellationSelect}
                        defaultValue={reschedulingRule}
                        control={control}
                        as={
                            <Select onChange={() => setReschedulingRule}>
                                <MenuItem key="do-not-allow" value="do-not-allow">
                                    Do not allow
                                </MenuItem>
                                <MenuItem key="allow" value="allow">
                                    Allow
                                </MenuItem>
                                <MenuItem key="late" value="late">
                                    Allow late
                                </MenuItem>
                            </Select>
                        }
                    />
                    <Typography style={{ marginRight: '16px' }}> rescheduling online.</Typography>
                    <InfoTooltip
                        title={
                            'If you allow late rescheduling online, when a client attempts to do this, they will see the Late reschedule warning text that you set below. The client must acknowledge this message by clicking a button before being able to complete the process. You will receive an automated email in the case of late reschedule (if you have the notification switched on).\n' +
                            'Deposits / late cancellation fees are not taken automatically - they must always be done manually.'
                        }
                    />
                </div>
            </FormControl>

            <BreakLines />

            <Typography variant="h4" className={classes.sectionTitleWrapper} style={{ fontWeight: 'bold' }}>
                Minimise Gaps
            </Typography>
            {/* 
            <BreakLines /> */}

            <div className={classes.inlineContainer}>
                <RadioGroup
                    style={{ display: 'flex', flexDirection: 'column' }}
                    name="minimiseGaps"
                    inputRef={register}
                    onChange={changeMinimiseGaps}
                >
                    <FormControlLabel
                        key={'minimise-gaps-show-all-available-slots'}
                        control={<Radio style={{ color: 'black' }} inputRef={register} />}
                        label={'Show all available slots'}
                        checked={!organisation?.minimiseGaps}
                        value={false}
                        disabled={false}
                    />
                    <FormControlLabel
                        style={{ display: 'none' }}
                        key={'minimise-gaps-show-all-available-slots'}
                        control={<Radio style={{ display: 'none' }} inputRef={register} />}
                        label={'Show all available slots'}
                        checked={organisation?.minimiseGaps}
                        value={true}
                        disabled={false}
                    />
                </RadioGroup>
            </div>
            <div className={classes.inlineContainer}>
                <RadioGroup
                    style={{ display: 'flex', flexDirection: 'column' }}
                    name="minimiseGapsStrategy"
                    inputRef={register}
                    onChange={changeMinimiseGaps}
                >
                    <FormControlLabel
                        key={'minimise-gaps-strategy-minimise-after-first-booked'}
                        control={<Radio style={{ color: 'black' }} inputRef={register} />}
                        label={'Show all slots until first appt booked, then minimise gaps'}
                        checked={
                            organisation?.minimiseGapsStrategy === MINIMISE_GAPS_STRATEGY.SHOW_ALL_SLOTS &&
                            organisation?.minimiseGaps
                        }
                        value={MINIMISE_GAPS_STRATEGY.SHOW_ALL_SLOTS}
                        disabled={false}
                    />
                    <FormControlLabel
                        key={'minimise-gaps-dont-how-all-available-slots'}
                        control={<Radio style={{ color: 'black' }} inputRef={register} />}
                        label={'Minimise gaps - show slots just before or after any existing appt, break or block'}
                        checked={
                            organisation?.minimiseGapsStrategy === MINIMISE_GAPS_STRATEGY.ALWAYS_MINIMISE &&
                            organisation?.minimiseGaps
                        }
                        value={MINIMISE_GAPS_STRATEGY.ALWAYS_MINIMISE}
                        disabled={false}
                    />
                </RadioGroup>
            </div>
            <BreakLines />
            <div>Also Show:</div>
            <div className={`${classes.inlineContainer} ${!organisation?.minimiseGaps ? classes.disabled : ''}`}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FormControlLabel
                        label={'First slot of the day'}
                        control={
                            <Checkbox
                                name={'alsoShowFirst'}
                                color="primary"
                                checked={organisation?.alsoShowFirst && organisation?.minimiseGaps}
                                value={true}
                                disabled={!organisation?.minimiseGaps}
                                onChange={changeAlsoShowFirst}
                                inputRef={register}
                            />
                        }
                    />
                    <FormControlLabel
                        label={'Last slot of the day'}
                        control={
                            <Checkbox
                                name={'alsoShowLast'}
                                color="primary"
                                checked={organisation?.alsoShowLast && organisation?.minimiseGaps}
                                value={true}
                                disabled={!organisation?.minimiseGaps}
                                onChange={changeAlsoShowLast}
                                inputRef={register}
                            />
                        }
                    />
                </div>
            </div>

            <BreakLines />

            <div className={classes.inlineContainer}>
                <Typography
                    variant="h4"
                    className={classes.sectionTitleWrapper}
                    style={{ fontWeight: 'bold', marginRight: '16px' }}
                >
                    Late cancellation / reschedule warning text
                </Typography>
                <InfoTooltip
                    title={
                        <Typography style={{ fontSize: 14 }}>
                            This text will be shown when trying to cancel/reschedule a booking within your specified
                            late cancellation period. Set the late cancellation period in the Calendar section above.
                        </Typography>
                    }
                    aria-label="help"
                />
            </div>
            {!isFirstLoading && (
                <div className={classes.row}>
                    <div>
                        <Editor
                            textRef={lateCancellationRef}
                            field="lateCancellationText"
                            defaultValue={late_cancellation_template}
                            organisation={organisation}
                            mergeTags={TC_MODAL_MERGE_TAGS}
                        />
                    </div>
                </div>
            )}

            <div className={classes.row} style={{ marginTop: '20px' }}>
                <Typography variant="h4" className={classes.marginTop} style={{ fontWeight: 'bold' }}>
                    Customise online text
                </Typography>
                <FormControlLabel
                    label="Show homepage banner text"
                    control={
                        <Checkbox
                            onChange={changeHomeBannerCheckbox}
                            color="primary"
                            checked={
                                organisation && organisation.showHomeBannerText
                                    ? organisation.showHomeBannerText
                                    : false
                            }
                            inputRef={register}
                            name="showHomeBannerText"
                        />
                    }
                />
                {organisation && organisation.showHomeBannerText && (
                    <div id="home-banner-editor">
                        <Editor
                            textRef={homeBannerRef}
                            field="homeBannerText"
                            placeholder="Input the text you want to be seen here"
                            organisation={organisation}
                        />
                    </div>
                )}
            </div>
            <BreakLines />
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                Booking Text Configuration
            </Typography>
            <Typography variant="body2" style={{ color: '#666' }}>
                Configure terms & conditions and thank you texts for different types of bookings. Select a booking type
                below to edit its corresponding texts.
            </Typography>
            <div className={classes.row}>
                <FormControl className={classes.selectField} style={{ maxWidth: '198px' }}>
                    <Typography variant="h4" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                        Select Booking Type
                    </Typography>
                    <Select value={selectedBookingType} onChange={handleBookingTypeChange} variant="outlined">
                        {Object.entries(BOOKING_TYPES).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                                {config.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <div
                ref={contentRef}
                style={{
                    height: containerHeight,
                    transition: 'opacity 0.3s ease, height 0.3s ease',
                    opacity: isTransitioning ? 0.5 : 1,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {isTransitioning && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1000
                        }}
                    >
                        <CircularProgress size={40} />
                    </div>
                )}

                <div style={selectedBookingType !== 'service' ? hiddenStyle : {}}>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        T&C text before completing online booking
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={termsAndConditionsRef}
                                    field="termsAndConditionsText"
                                    organisation={organisation}
                                    mergeTags={TC_MERGE_TAGS}
                                />
                            </div>
                        </div>
                    )}

                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        Thank you text after completing online booking
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor textRef={thankYouRef} field="thankYouText" organisation={organisation} />
                            </div>
                        </div>
                    )}
                </div>

                <div style={selectedBookingType !== 'course' ? hiddenStyle : {}}>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        T&C text before purchasing course online
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={termsAndConditionsCourseRef}
                                    field="termsAndConditionsTextCourse"
                                    organisation={organisation}
                                    mergeTags={TC_MERGE_TAGS}
                                />
                            </div>
                        </div>
                    )}

                    <BreakLines />
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        Thank you text after purchasing course online
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={thankYouCourseRef}
                                    field="thankYouTextCourse"
                                    organisation={organisation}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div style={selectedBookingType !== 'courseService' ? hiddenStyle : {}}>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        T&C text before completing online course service booking
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={termsAndConditionsCourseServiceRef}
                                    field="termsAndConditionsTextCourseService"
                                    organisation={organisation}
                                    mergeTags={TC_MERGE_TAGS}
                                />
                            </div>
                        </div>
                    )}

                    <BreakLines />
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        Thank you text after completing online course service booking
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={thankYouCourseServiceRef}
                                    field="thankYouTextCourseService"
                                    organisation={organisation}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div style={selectedBookingType !== 'giftVoucher' ? hiddenStyle : {}}>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        T&C text before purchasing gift voucher online
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={termsAndConditionsGiftVoucherRef}
                                    field="termsAndConditionsTextGiftVoucher"
                                    organisation={organisation}
                                    mergeTags={TC_MERGE_TAGS}
                                />
                            </div>
                        </div>
                    )}

                    <BreakLines />
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        Thank you text after purchasing gift voucher online
                    </Typography>
                    {!isFirstLoading && (
                        <div className={classes.row}>
                            <div>
                                <Editor
                                    textRef={thankYouGiftVoucherRef}
                                    field="thankYouTextGiftVoucher"
                                    organisation={organisation}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default withStyles(settingsStyles)(CustomerAppTab);
